<script setup lang="ts">
import { nextTick, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "primevue/usetoast";
import Toast from "primevue/toast";
import { LanguageManagementAPI } from "../api";
import { getDisplayName } from "../main.ts";
import { TextSearchHit } from "../types/PersologOnlineTypes.ts";

// The text maintenance workflow is staging-based (edits land on staging, then get released to
// prod), so the search targets staging as well.
const STAGE = "STAGING";

const { t } = useI18n();
const toast = useToast();
const search = ref("");
type LanguageOption = { name: string; code: string };
const languages = ref<LanguageOption[]>([]);
const selectedLanguages = ref<string[]>([]);
const searchResults = ref<TextSearchHit[]>([]);
const isSearching = ref(false);
const hasSearched = ref(false);
const searchError = ref(false);
const lastQuery = ref("");
const searchInput = ref<any>(null);

function focusSearch() {
    nextTick(() => {
        const el =
            searchInput.value?.$el instanceof HTMLInputElement
                ? (searchInput.value.$el as HTMLInputElement)
                : (searchInput.value?.$el?.querySelector?.("input") as HTMLInputElement | null);
        el?.focus();
    });
}

function clearSearch() {
    search.value = "";
    focusSearch();
}

// One server-side request does the matching (identifier + text, case-insensitive), so the search
// no longer fans out a request per (namespace, language) — which used to time out and silently
// return nothing as the number of products/languages grew.
async function searchText() {
    const query = search.value.trim();
    lastQuery.value = query;
    if (!query) {
        searchResults.value = [];
        hasSearched.value = true;
        return;
    }
    isSearching.value = true;
    searchError.value = false;
    try {
        searchResults.value = await LanguageManagementAPI.searchTexts(STAGE, query, selectedLanguages.value);
    } catch (error) {
        console.error("Text search failed:", error);
        searchResults.value = [];
        searchError.value = true;
        toast.add({
            severity: "error",
            summary: t("textSearch.error.searchFailed"),
            detail: t("textSearch.error.searchFailedDetail"),
            life: 5000,
        });
    } finally {
        hasSearched.value = true;
        isSearching.value = false;
    }
}

// Resolve a human-readable language name via the shared helper, which splits region suffixes such
// as "zh_CN" (Intl.DisplayNames.of() throws on the underscore form). Guard per code so a single
// unrecognised code can't throw and blank the whole dropdown.
function languageLabel(code: string): string {
    try {
        return getDisplayName(code) ?? code;
    } catch {
        return code;
    }
}

onMounted(async () => {
    focusSearch();
    const codes = await LanguageManagementAPI.getLanguages();
    languages.value = codes
        .map((code) => ({ name: languageLabel(code), code }))
        .sort((a, b) => a.name.localeCompare(b.name));
});
</script>

<template>
    <Toast />
    <div class="text-diffs-container">
        <div class="page-header mb-6">
            <h1 class="page-title" data-cy="text-diffs-title">{{ t("textSearch.title") }}</h1>
            <p class="page-description" data-cy="text-diffs-description">
                {{ t("textSearch.description") }}
            </p>
        </div>
    </div>

    <div class="flex flex-wrap gap-4 justify-start">
        <div class="basis-full md:basis-1/2">
            <InputGroup>
                <FloatLabel variant="on">
                    <InputText
                        ref="searchInput"
                        class="w-full"
                        id="text-search_input_search"
                        v-model="search"
                        @keyup.enter="searchText"
                    />
                    <label for="text-search_input_search">{{ t("header.navBar.textSearch") }}</label>
                </FloatLabel>
                <Button icon="pi pi-times" outlined severity="secondary" @click="clearSearch" />
            </InputGroup>
        </div>

        <div class="basis-full md:basis-1/4">
            <FloatLabel class="w-full md:w-80" variant="on">
                <MultiSelect
                    id="text-search_multi-select-languages"
                    v-model="selectedLanguages"
                    display="chip"
                    :options="languages"
                    optionLabel="name"
                    optionValue="code"
                    filter
                    :maxSelectedLabels="3"
                    class="w-full"
                />
                <label for="text-search_multi-select-languages">
                    {{ t("textSearch.language.placeholder") }}
                </label>
            </FloatLabel>
        </div>

        <div class="basis-full md:basis-1/6">
            <Button id="text-search_btn_search" :label="t('buttons.find')" icon="pi pi-search" :loading="isSearching" @click="searchText" />
        </div>
    </div>

    <p
        v-if="hasSearched && !isSearching && !searchError && searchResults.length"
        class="mt-2"
        data-cy="text-search_result-count"
    >
        {{ t("textSearch.resultCount", { count: searchResults.length }) }}
    </p>
    <p
        v-else-if="hasSearched && !isSearching && !searchError && !searchResults.length"
        class="mt-2"
        data-cy="text-search_no-results"
    >
        {{ t("textSearch.noResults", { query: lastQuery }) }}
    </p>

    <DataTable :value="searchResults" tableStyle="min-width: 50rem" class="mt-6">
        <Column field="product" :header="t('textMaintenance.product')"></Column>
        <Column field="language" :header="t('projects.language')"></Column>
        <Column field="identifier" :header="t('textMaintenance.table.identifier')"></Column>
        <Column field="text" header="Text"></Column>
        <Column :header="t('textMaintenance.actions')">
            <template #body="slotProps">
                <Button
                    as="a"
                    style="text-decoration: none"
                    data-cy="text-search_maintenance-link"
                    :href="'/text-maintenance?product=' + slotProps.data.product + '&language=' + slotProps.data.language"
                    icon="pi pi-external-link"
                />
            </template>
        </Column>
    </DataTable>
</template>
